import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Grid from '@mui/material/Grid2'
import newWithSimpleModal from 'Components/reusable/HOC/newWithSimpleModal'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileDropzone } from 'Components/reusable/InputFields/Dropzone/FileDropzone'
import { IError } from 'types/error'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import { cvParserAPI } from 'api/CvParserAPI'
import CaleoPrimaryButton from 'Components/reusable/Buttons/CaleoPrimaryButton'
import { useLanguageData } from 'hooks'
import { PersonId } from 'types/ids'
import { ICv } from 'types/cvsInterfaces'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'
import { IParsedJSON } from 'types/cvParserInterfaces'
import EditData from 'Components/General/Admin/AdminParse/EditData'

/** @notExported */
interface ICvParseModalProps {
  /** The ID of the person. */
  personId: PersonId
  /** Callback function to close the modal. */
  onClose: (cv?: ICv) => void
}

/**
 * Modal component for parsing CV.
 *
 * @param personId - The ID of the person.
 * @param onClose - Callback function to close the modal.
 * @returns The CV parse modal component.
 * @notExported
 */
const CvParseModal: React.FC<ICvParseModalProps> = ({ personId, onClose }) => {
  const { t } = useTranslation()
  const { languages } = useLanguageData()

  const [cv, setCv] = useState<File | null>(null)
  const [result, setResult] = useState<IParsedJSON>()
  const [loading, setLoading] = useState<boolean>(false)
  const [backendError, setBackendError] = useState<IError>()

  /**
   * Polls the job status endpoint until parsing is complete
   *
   * @param jobId - Job ID from initial parse request
   * @notExported
   */
  const pollParseStatus = async (jobId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await cvParserAPI.getParseStatus(jobId)

        if (status.status === 'completed' && status.results) {
          clearInterval(pollInterval)
          setResult(status.results[0])
          setLoading(false)
        } else if (status.status === 'error') {
          clearInterval(pollInterval)
          setBackendError({
            status: 500,
            error: new Error(status.error || 'Parsing failed'),
            key: 'error.parse',
            id: '',
          } as IError)
          setLoading(false)
        }
      } catch (error) {
        clearInterval(pollInterval)
        setBackendError(error as IError)
        setLoading(false)
      }
    }, 5000) // Poll every 5 seconds
  }

  const sendFile = async () => {
    if (cv) {
      try {
        setLoading(true)
        const formData = new FormData()

        formData.append('file', cv)
        formData.append('personId', `${personId}`)

        // Start async parsing and get job ID
        const { jobId } = await cvParserAPI.parseSingleCV(formData)

        // Start polling for job status
        await pollParseStatus(jobId)
      } catch (error) {
        const errorObj = error as IError

        // Handle cost limit errors with i18n keys
        if (
          errorObj.response?.data?.error?.key &&
          (errorObj.response.data.error.key === 'parsing.error.dailyCostLimitExceeded' ||
            errorObj.response.data.error.key === 'parsing.error.monthlyCostLimitExceeded')
        ) {
          const { key } = errorObj.response.data.error
          setBackendError({
            status: 429,
            error: new Error(t(key)),
            key: key,
            id: '',
          } as IError)
        } else {
          setBackendError(errorObj)
        }

        setLoading(false)
      }
    }
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setCv(acceptedFiles[0])
  }, [])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <>
      <DialogContent>
        <Grid sx={{ display: !result && !loading ? 'inherit' : 'none' }}>
          <FileDropzone
            onDrop={onDrop}
            accept={{
              'application/pdf': ['.pdf'],
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            }}
            oldFile={cv?.name}
            dragHint={t('dropbox.dragAttachment')}
            dropHint={t('dropbox.dropAttachment')}
            width="390px"
          />
        </Grid>
        {loading && <LoadingIndicator message="parsing" />}
        {result && languages && (
          <Grid>
            <EditData data={result} setData={setResult} languages={languages} existingPerson={true} />
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <CaleoPrimaryButton
          testId={!result ? 'button-cv-parse-send' : 'button-cv-parse-close'}
          loading={loading}
          clickAction={() => {
            if (!result) {
              sendFile()
            } else {
              onClose(result.createdCV)
            }
          }}
          label={!result ? t('send') : t('close')}
          valid={!result ? !!cv : true}
        />
      </DialogActions>
    </>
  )
}

export default newWithSimpleModal(CvParseModal)
